import { Injectable, OnDestroy } from '@angular/core';

import {
  BehaviorSubject,
  Observable,
  Subject,
  distinctUntilChanged,
  fromEvent,
  takeUntil,
} from 'rxjs';

@Injectable()
export class MwFormUIService implements OnDestroy {
  get sidebarContainerHeight$(): Observable<number> {
    return this._sidebarContainerHeight$.asObservable();
  }

  get rootContainerHeight$(): Observable<number> {
    return this._rootContainerHeight$
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  get formContainerWidth$(): Observable<number> {
    return this._formContainerWidth$
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  get rootContainerScrollTop$(): Observable<number> {
    return this._rootContainerScrollTop$.asObservable();
  }

  private readonly resizeObserver = new ResizeObserver((entries) =>
    this.handleResizes(entries)
  );

  private _sidebarContainerHeight$ = new Subject<number>();
  private _rootContainerHeight$ = new Subject<number>();
  private _formContainerWidth$ = new Subject<number>();
  private _rootContainerScrollTop$ = new BehaviorSubject<number>(0);
  private _unsubscribeAll$ = new Subject<void>();
  private _rootContainerEl: HTMLElement;
  private _formContainerEl: HTMLElement;
  private _sidebarContainerEl: HTMLElement;
  private _stickyBarContainerEl: HTMLElement;

  static computeHeight(el: HTMLElement): number {
    const styles = window.getComputedStyle(el);
    return (
      el.getBoundingClientRect().height -
      parseInt(styles.paddingTop, 10) -
      parseInt(styles.paddingBottom, 10)
    );
  }

  static observeHeight(el: HTMLElement): BehaviorSubject<number> {
    const result = new BehaviorSubject<number>(0);
    const resizeObserver = new ResizeObserver((entries) =>
      result.next(MwFormUIService.computeHeight(el))
    );

    resizeObserver.observe(el);

    result.subscribe({
      complete: () => resizeObserver.disconnect(),
    });

    return result;
  }

  ngOnDestroy(): void {
    this.resizeObserver.disconnect();
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  setRootContainer(rootContainer: HTMLElement): void {
    if (!this._rootContainerEl) {
      this._rootContainerEl = rootContainer;
      this.resizeObserver.observe(rootContainer);

      fromEvent(rootContainer, 'scroll')
        .pipe(takeUntil(this._unsubscribeAll$))
        .subscribe((e: Event) => {
          const el: HTMLElement = event.target as HTMLElement;
          const scrollTop = el.scrollTop;

          this._rootContainerScrollTop$.next(scrollTop);
        });

      this.formContainerWidth$
        .pipe(takeUntil(this._unsubscribeAll$))
        .subscribe(() => this.updateStickyBarSize());
    }
  }

  setFormContainer(el: HTMLElement): void {
    if (!this._formContainerEl) {
      this._formContainerEl = el;
      this.resizeObserver.observe(el);
    }
  }

  setSidebarContainer(el: HTMLElement): void {
    if (!this._sidebarContainerEl) {
      this._sidebarContainerEl = el;
      this.resizeObserver.observe(el);
    }
  }

  setStickyBarContainer(el: HTMLElement): void {
    if (this._stickyBarContainerEl !== el) {
      this._stickyBarContainerEl = el;
      this.updateStickyBarSize();
    }
  }

  dispose(el: HTMLElement): void {
    this.resizeObserver.unobserve(el);

    if (this._sidebarContainerEl === el) {
      this._sidebarContainerEl = null;
    }

    if (this._stickyBarContainerEl === el) {
      this._stickyBarContainerEl = null;
    }

    if (this._rootContainerEl === el) {
      this._rootContainerEl = null;
    }

    if (this._formContainerEl === el) {
      this._formContainerEl = null;
    }
  }

  private updateStickyBarSize(): void {
    if (!this._stickyBarContainerEl?.parentElement || !this._formContainerEl) {
      return;
    }
    const barLeft =
      this._stickyBarContainerEl.parentElement.getBoundingClientRect().left;

    const formRight = this._formContainerEl.getBoundingClientRect().right;
    const barWidth = formRight - barLeft;

    this._stickyBarContainerEl.setAttribute('style', `width: ${barWidth}px`);
  }

  private handleResizes(entries: ResizeObserverEntry[]): void {
    entries.forEach((t) => {
      if (t.target === this._rootContainerEl) {
        this._rootContainerHeight$.next(t.contentRect.height);
      }
      if (t.target === this._formContainerEl) {
        this._formContainerWidth$.next(t.contentRect.width);
      }
      if (t.target === this._sidebarContainerEl) {
        this._sidebarContainerHeight$.next(t.contentRect.height);
      }
    });
  }
}
